import React, { useState, useEffect, useMemo } from "react";
import { Typography, Form, Select, Flex, Button, Card } from "antd";
import moment from "moment";
import MasterComponent from "../../../components/Forms";
import { MasterConfigs } from "../../../interfaces/entity";
import ProfileCard from "../../../components/ContactInfo/ProfileCard";
import { constants } from "../../../constants/common";
import DrawerComponent from "../../../components/Forms/Drawer";
import { EditOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import ContactDetails from "./ContactDetails";
import { createActivitiesColumn } from "../../../utils/activitiesColumn";
const { Text, Title } = Typography;

const Contacts = () => {
    const [openDrawer, setOpenDrawer] = React.useState(false);
    const [selectedContact, setSelectedContact] = React.useState(null);
    const [selectedID, setSelectedID] = React.useState(null);

    // States for upcoming activities
    const [upcomingActivities, setUpcomingActivities] = useState<{ [key: string]: { date: string | Date; hasUpcoming: boolean } }>({});
    const dispatch = useDispatch();
    const { refreshTable, user } = useSelector((app: any) => app?.app || {});
    const [tableData, setTableData] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>(null);
    const isAdmin = user?.designation === 'Admin';

    // Refresh upcoming activities when table refreshes
    useEffect(() => {
        if (refreshTable && tableData.length > 0) {
            extractUpcomingActivitiesFromRecords(tableData);
        }
    }, [refreshTable, tableData.length]);

    // Extract upcoming activities info from contacts API response
    const extractUpcomingActivitiesFromRecords = (records: any[]) => {
        if (!records || records.length === 0) return;

        const activitiesMap: { [key: string]: { date: string | Date | null; hasUpcoming: boolean } } = {};

        records.forEach((record) => {
            if (!record?._id) return;
            const recordKey = `contacts-${record._id}`;

            // Use upcomingActivity from API response if available
            if (record.upcomingActivity) {
                activitiesMap[recordKey] = {
                    date: record.upcomingActivity.upcomingDate || null,
                    hasUpcoming: record.upcomingActivity.hasUpcoming || false
                };
            } else {
                // Default to no upcoming activities if not provided
                activitiesMap[recordKey] = {
                    date: null,
                    hasUpcoming: false
                };
            }
        });

        setUpcomingActivities(prev => ({
            ...prev,
            ...activitiesMap
        }));
    };

    const config: MasterConfigs = useMemo(() => {
        return {
            api: () => '/contacts',
            queryParams: {
                andOr: 'and', // Use 'and' to combine filters
            },
            title: 'Contacts',
            setInitialForm: () => {
                // For normal users, set default contactOwner to current user when creating new contact
                if (!isAdmin && user?._id) {
                    return { contactOwner: user._id };
                }
                return {};
            },
            activitiesColumn: {
                enabled: true,
                modelName: 'contacts',
                title: 'Activities',
                width: 120,
                fixed: 'left',
                align: 'center',
                color: 'blue'
            },
            onDataLoad: (data: any) => {
                // Store table data and extract upcoming activities info from API response
                const records = Array.isArray(data) ? data : (data?.data || []);
                if (records && Array.isArray(records) && records.length > 0) {
                    setTableData(records);
                    extractUpcomingActivitiesFromRecords(records);
                }
            },
            onSummaryLoad: (summaryData: any) => {
                setSummary(summaryData);
            },
            renderSummary: () => {
                if (!summary) return null;
                return (
                    <Flex gap={12} wrap="wrap" style={{ width: '100%', marginBottom: 16 }}>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #1890ff' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Total Contacts:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.total || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #52c41a' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Today:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.today || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #faad14' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Last Week:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.lastWeek || 0}
                                </Title>
                            </Flex>
                        </Card>
                        <Card
                            size="small"
                            style={{ flex: 1, minWidth: 200, maxWidth: 300, backgroundColor: '#fff', borderLeft: '4px solid #722ed1' }}
                        >
                            <Flex align="center" gap={8} style={{ justifyContent: 'space-between' }}>
                                <Text type="secondary" style={{ fontSize: '12px', margin: 0 }}>
                                    Last Month:
                                </Text>
                                <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
                                    {summary.lastMonth || 0}
                                </Title>
                            </Flex>
                        </Card>
                    </Flex>
                );
            },
            getFilterColumns: () => {
                const filterColumns: any[] = [
                    {
                        label: 'Lead Source',
                        name: 'leadSource',
                        type: 'select',
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Source',
                            showSearch: true,
                        },
                        children: constants.LEAD_SOURCES.map(source => ({
                            name: source.name,
                            value: source.value
                        }))
                    },
                ];

                // Contact Owner filter - only visible to Admin
                if (isAdmin) {
                    filterColumns.push({
                        label: 'Contact Owner',
                        name: 'contactOwner',
                        type: 'select',
                        isCombo: true,
                        span: 4,
                        selectProps: {
                            allowClear: true,
                            placeholder: 'Select Contact Owner',
                            showSearch: true,
                        },
                    });
                }

                filterColumns.push({
                    label: 'Business',
                    name: 'business',
                    type: 'select',
                    isCombo: true,
                    span: 4,
                    selectProps: {
                        allowClear: true,
                        placeholder: 'Select Business',
                        showSearch: true,
                    },
                });

                filterColumns.push({
                    label: 'Email',
                    name: 'email',
                    type: 'text',
                    span: 4,
                    placeholder: 'Search by email',
                });

                filterColumns.push({
                    label: 'Phone',
                    name: 'phone',
                    type: 'text',
                    span: 4,
                    placeholder: 'Search by phone',
                });

                // Date range filters for created date
                filterColumns.push({
                    label: 'Start Date',
                    name: 'startDate',
                    type: 'date',
                    span: 4,
                    placeholder: 'Select start date',
                });
                filterColumns.push({
                    label: 'End Date',
                    name: 'endDate',
                    type: 'date',
                    span: 4,
                    placeholder: 'Select end date',
                });

                return filterColumns;
            },
            modalTitle: (text) => text + ' Contact',
            isActionTab: true,
            getColumns: () => {
                const columns: any[] = [];

                // Add Activities column if enabled in config
                const activitiesColumn = createActivitiesColumn(config.activitiesColumn);
                if (activitiesColumn) {
                    columns.push(activitiesColumn);
                }

                // Add other columns
                columns.push(
                    {
                        title: 'Contact Name',
                        dataIndex: '',
                        key: '',
                        width: 200,
                        fixed: 'left',
                        render: (text, record) => <div onClick={() => handleLeadClick(record)}>
                            <SmallProfileCard
                                name={[record?.preName, record?.firstName, record?.lastName].filter(Boolean).join(' ')}
                                avatar={record?.profilePhoto ? record?.profilePhoto : undefined}
                                subTitle={record?.title}
                            />
                        </div>
                    },
                    {
                        title: 'Company',
                        dataIndex: 'business',
                        key: 'business',
                        width: 150,
                        render: (text) => text?.businessName || 'N/A',
                        ellipsis: true,
                    },
                    {
                        width: 150,
                        title: 'Email',
                        dataIndex: 'email',
                        key: 'email',
                    },
                    {
                        width: 150,
                        title: 'Phone',
                        dataIndex: 'phone',
                        key: 'phone',
                    },
                    {
                        title: 'Contact Owner',
                        dataIndex: 'contactOwner',
                        key: 'contactOwner',
                        render: (text) => <SmallProfileCard
                            name={constants.GET_FULL_NAME(text)}
                            avatar={text?.avatar ? text?.avatar : undefined}
                            subTitle={text?.employeeID || text?.workEmail}
                        />,
                        width: 150
                    },
                    {
                        title: 'Created At',
                        dataIndex: 'createdAt',
                        key: 'createdAt',
                        render: (text) => moment(text).format('DD MMM YYYY HH:mm'),
                        align: 'center',
                        width: 150,
                    }
                );

                return columns;
            },
            getFormColumns: () => [
                {
                    label: 'Profile Photo',
                    name: 'profilePhoto',
                    type: 'file',
                    uploadProps: {
                        accept: 'image/*',
                        maxCount: 1,
                        listType: 'picture-circle',
                    }
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Contact Information</Title>
                },
                {
                    label: 'Contact Owner',
                    name: 'contactOwner',
                    type: 'select',
                    isCombo: true,
                    span: 12,
                },
                {
                    label: 'Lead Source',
                    name: 'leadSource',
                    type: 'select',
                    span: 12,
                    selectProps: {
                        showSearch: true,
                        optionFilterProp: 'children',
                        allowClear: true,
                    },
                    children: constants.LEAD_SOURCES
                },
                {
                    label: 'First Name',
                    name: 'firstName',
                    type: 'text',
                    inputProps: {
                        addonBefore: <Form.Item name="preName">
                            <Select style={{ width: 100 }} placeholder="Title">
                                {constants.PRENAME.map(ele => <Select.Option key={ele.value} value={ele.value}>{ele.name}</Select.Option>)}
                            </Select>
                        </Form.Item>
                    },
                    rules: [{ required: true, message: 'First Name is required' }],
                    span: 12,
                },
                {
                    label: 'Last Name',
                    name: 'lastName',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Account Name',
                    name: 'business',
                    type: 'select',
                    isCombo: true,
                    span: 12,
                },
                {
                    label: 'Vendor Name',
                    name: 'vendor',
                    type: 'select',
                    span: 12,
                },
                {
                    label: 'Email',
                    name: 'email',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                return emailRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid email address'));
                            },
                        },
                    ],
                },
                {
                    label: 'Title',
                    name: 'title',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Phone',
                    name: 'phone',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const phoneRegex = /^[0-9]{10,15}$/;
                                return phoneRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid phone number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Other Phone',
                    name: 'secondaryPhone',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const phoneRegex = /^[0-9]{10,15}$/;
                                return phoneRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid phone number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Home Phone',
                    name: 'homePhone',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const phoneRegex = /^[0-9]{10,15}$/;
                                return phoneRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid phone number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Department',
                    name: 'department',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Fax',
                    name: 'fax',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const faxRegex = /^[0-9+\-().\s]{6,20}$/;
                                return faxRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid fax number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Mobile',
                    name: 'mobile',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const mobileRegex = /^[0-9]{10,15}$/;
                                return mobileRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid mobile number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Assistant Name',
                    name: 'assistantName',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Assistant Phone',
                    name: 'assistantPhone',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const phoneRegex = /^[0-9]{10,15}$/;
                                return phoneRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid phone number'));
                            },
                        },
                    ],
                },
                {
                    label: 'Birthday',
                    name: 'birthday',
                    type: 'date',
                    span: 12,
                },
                {
                    label: 'Email Opt Out ',
                    name: 'emailOptOut',
                    type: 'checkbox',
                    span: 12,
                },
                {
                    label: 'Secondary Email',
                    name: 'secondaryEmail',
                    span: 12,
                    type: 'text',
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                return emailRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid email address'));
                            },
                        },
                    ],
                },
                {
                    label: 'Report To',
                    name: 'reportTo',
                    type: 'select',
                    isCombo: true,
                    span: 12,
                },
                {
                    label: 'Twitter',
                    name: 'twitter',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'LinkedIn Profile URL',
                    name: 'linkedinProfileUrl',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                try {
                                    const url = new URL(value);
                                    if (url.hostname.includes('linkedin.com')) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                                } catch {
                                    return Promise.reject(new Error('Enter a valid LinkedIn profile URL'));
                                }
                            },
                        },
                    ],
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Address Information</Title>
                },
                {
                    label: 'Street',
                    name: 'street',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'City',
                    name: 'city',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'State',
                    name: 'state',
                    type: 'text',
                    span: 12,
                },
                {
                    label: 'Zip Code',
                    name: 'zipCode',
                    type: 'text',
                    span: 12,
                    rules: [
                        {
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                const zipRegex = /^[0-9]{4,10}$/;
                                return zipRegex.test(value)
                                    ? Promise.resolve()
                                    : Promise.reject(new Error('Enter a valid zip code'));
                            },
                        },
                    ],
                },
                {
                    label: 'Country',
                    name: 'country',
                    type: 'text',
                    span: 12,
                },
                {
                    type: 'custom',
                    span: 24,
                    formItem: (form) => <Title level={5} className="font-bold border-b pb-2">Description Information</Title>
                },
                {
                    label: 'Description',
                    name: 'description',
                    type: 'textarea',
                    span: 24,
                    textAreaProps: {
                        autoSize: { minRows: 3, maxRows: 6 },
                    }
                },

            ],
            combo: [
                {
                    api: '/contacts',
                    name: 'reportTo',
                    idKey: '_id',
                    render: (text) => constants.GET_FULL_NAME(text),
                },
                {
                    api: '/accounts',
                    name: 'business',
                    idKey: '_id',
                    render: (text) => text?.businessName || 'N/A',
                },
                // Contact Owner combo - always include, but for normal users, default to current user
                {
                    api: '/users',
                    name: 'contactOwner',
                    idKey: '_id',
                    render: (text) => <ProfileCard
                        size={30}
                        name={constants.GET_FULL_NAME(text)}
                        avatarUrl={text?.avatar ? process.env.FILE_URL + text?.avatar : null}
                        role={null}
                    />
                },
            ],
            actions: [
                {
                    actionType: 'filter',
                    name: 'Filter',
                },
                {
                    name: 'Add Contact',
                    actionType: 'Add',
                }
            ]
        };
    }, [summary, isAdmin]);

    const handleLeadClick = (record) => {
        setSelectedContact(record);
        setOpenDrawer(true);
    }

    const handleEditClick = (record) => {
        setSelectedID(record?._id);
        setOpenDrawer(true);
    }

    return (
        <>
            <MasterComponent config={config} isModal={true} recordId={selectedID} />
            <DrawerComponent
                title={
                    <Flex justify="space-between" align="center" gap={2} style={{ width: '95%' }}>
                        <ProfileCard
                            size={40}
                            name={[selectedContact?.preName, selectedContact?.firstName, selectedContact?.lastName].filter(Boolean).join(' ')}
                            avatarUrl={selectedContact?.profilePhoto ? process.env.FILE_URL + selectedContact?.profilePhoto : null}
                            role={selectedContact?.title || 'Contact Details'}
                        />
                        {!selectedContact?.converted && <div className="flex gap-2">
                            <Button disabled type="primary">Send Email</Button>
                            <Button icon={<EditOutlined />} onClick={() => handleEditClick(selectedContact)} />
                        </div>}
                    </Flex>
                }
                visible={openDrawer}
                handleCancel={() => setOpenDrawer(false)}
                isView={true}
                loading={false}
                submitForm={() => { }}
                rest={{
                    width: '90%', style: {
                        background: '#eef1f9',
                        padding: '0px !important',
                    },
                    bodyStyle: { padding: 0 },
                    headerStyle: { background: '#FFFFFF', padding: '8px 10px' },
                }}
            >
                <ContactDetails contact={selectedContact} />
            </DrawerComponent>
        </>
    );
}

export default Contacts;